import type { LinkOptions } from '@tanstack/react-router';

import { useSmUp } from '@/shared/hooks/useSmUp';
import { filePreviewLink } from './filePreviewLink';

/**
 * The single place this app decides "open the preview panel for this workspace
 * path" (spec 140 FR-050).
 *
 * Owns, once and only once: the `sm` breakpoint read, the sub-`sm`
 * navigate-away fallback to the Files route, and the push-versus-replace
 * distinction. Every entry point calls this; no call site reads `useSmUp` or
 * writes a `preview` search param by hand — which is what keeps SC-020's
 * "exactly one `matchMedia` subscription" literally true, and what made nine
 * entry points affordable instead of nine copies of a ternary.
 *
 * It is a **hook**, not a plain helper, because the breakpoint is a
 * subscription rather than a value: a reader who resizes the window gets the
 * destination their current width warrants on their next click (FR-035b),
 * rather than at the next reload.
 *
 * It returns link *options*, so every consumer stays a `<Link {...}>` and
 * nothing grows an `onClick` that navigates — the destination is in the href,
 * which is what makes these links openable in a new tab and readable on hover.
 *
 * `previewPath` is the address's current `preview` param, and it decides only
 * push versus replace (FR-040): opening the panel pushes one history entry,
 * swapping the file showing in an already-open panel replaces it, so glancing
 * at six files does not leave six entries to walk back through.
 *
 * Non-component callers — `fileChangeLink` — are plain modules and cannot call
 * a hook. They take the returned function as a required
 * argument from their calling component rather than growing a second
 * `matchMedia` subscription (FR-050's last sentence).
 */
export function useWorkspaceFilePreviewLink(
  workspaceId: string,
  previewPath: string | undefined,
): (path: string) => LinkOptions {
  const smUp = useSmUp();

  return (path: string) =>
    smUp
      ? ({
          // Same route, same open task: only the `preview` param changes, and
          // every unrelated param (`task`, `tab`, `labels`, `q`, `item`) is
          // carried through by the updater rather than replaced by an object
          // that happens to hold only this one.
          to: '.',
          search: (prev: Record<string, unknown>) => ({
            ...prev,
            preview: path,
          }),
          replace: previewPath !== undefined,
        } as LinkOptions)
      : // Below `sm` there is no room for a panel beside anything, so the
        // destination is the Files route itself — decided here, at the link,
        // rather than by the panel declining to render, so no address ever
        // carries a `preview` the viewport cannot honour (FR-035a/FR-035b).
        filePreviewLink(workspaceId, path);
}
